import { useCallback, useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import type { ColumnsType } from "antd/es/table";
import { Table } from "antd";
import CustomEmpty from "components/CustomEmpty";
import { useGetDashboardJobs } from "../hooks/useGetDashboardJobs";
import { useGetRecommendedJobs } from "../hooks/useGetRecommendedJobs";
import { routePath } from "config/routeMgt/routePath";
import { Link, useNavigate, useParams } from "react-router-dom";
import SearchInput from "./SearchInput";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";
import FeedbackPopup from "features/feedback/components/FeedbackPopup";
import { useGetPendingFeedbackJobs } from "features/feedback/hooks/useGetPendingFeedbackJobs";
import { IFeedbackJob } from "features/feedback/types";

interface JobType {
  id: string;
  slug?: string;
  title: string;
  created_at: any;
  status: string;
  likes_count: any;
}

interface TalentType {
  result: any;
}

export const RecruiterChart = ({ stats }: any) => {
  const { token, userInfo } = useGetUserInfo();
  const [chartHeight, setChartHeight] = useState(350);

  const [jobsSearch, setJobsSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [talentSearch, setTalentSearch] = useState("");
  const { data, isLoading } = useGetDashboardJobs(token);
  const { data: dataRec } = useGetRecommendedJobs(token);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const { data: jobsData, isLoading: isFeedbackLoading } =
    useGetPendingFeedbackJobs({});
  const allJobs = (jobsData || []) as IFeedbackJob[];

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 480) {
        setChartHeight(200);
      } else if (window.innerWidth < 640) {
        setChartHeight(250);
      } else if (window.innerWidth < 768) {
        setChartHeight(300);
      } else {
        setChartHeight(350);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const series = [
    {
      name: "Sent",
      data: stats?.stats?.sent_messages?.sent ?? [],
    },
    {
      name: "Received",
      data: stats?.stats?.received_messages?.received ?? [],
    },
  ];

  const options: any = {
    chart: {
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: true,
      },
      fontFamily: "Inter",
    },

    colors: ["#111B75", "#22C55E"],

    dataLabels: {
      enabled: false,
    },

    stroke: {
      curve: "straight",
      width: 2,
    },

    markers: {
      size: 0,
      hover: {
        size: 4,
      },
    },

    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      fontFamily: "Inter",

      markers: {
        width: 6,
        height: 6,
        radius: 10,
      },

      itemMargin: {
        horizontal: 8,
      },
    },

    tooltip: {
      theme: "light",
      shared: true,
      intersect: false,
    },

    grid: {
      show: true,
      borderColor: "#F1F1F1",
      strokeDashArray: 0,

      xaxis: {
        lines: {
          show: false,
        },
      },

      yaxis: {
        lines: {
          show: true,
        },
      },
    },

    yaxis: {
      show: false,
    },

    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],

      labels: {
        rotate: 0,
        hideOverlappingLabels: true,

        style: {
          colors: "#94A3B8",
          fontFamily: "Inter",
          fontSize: "11px",
        },
      },

      axisBorder: {
        show: false,
      },

      axisTicks: {
        show: false,
      },

      tooltip: {
        enabled: false,
      },
    },
  };

  const jobsColumns: ColumnsType<JobType> = [
    {
      title: "",
      dataIndex: "title",
      key: "title",
      render: (_, val) => (
        <div className="flex items-center justify-between w-full">
          <div>
            <Link
              to={routePath.jobDetails(val.slug).path}
              className="font-medium text-primary block"
            >
              {val.title}
            </Link>
            <div className="text-xs text-gray-500">
              {formatDate(val.created_at)}
            </div>
          </div>

          <div className="flex flex-col items-center">
            <span className="bg-indigo-100 text-indigo-600 text-xs font-semibold px-2 py-1 rounded-full">
              {val.likes_count}
            </span>
            <span className="text-[10px] text-gray-500 mt-1">Applied</span>
          </div>
        </div>
      ),
    },
  ];

  const shouldSkip = id === "skipRecruitmentFeedback";

  useEffect(() => {
    if (!shouldSkip && allJobs.length > 0) {
      setShowPopup(true);
    }
  }, [allJobs]);

  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
  }, []);

  const handleProvideFeedback = useCallback(
    (job: IFeedbackJob) => {
      navigate(`/feedback/${job.id}`, {
        state: {
          job,
        },
      });
    },
    [navigate],
  );

  const filteredJobs = data?.filter(
    (job) =>
      job.title?.toLowerCase().includes(jobsSearch.toLowerCase()) &&
      (statusFilter.length === 0 || statusFilter.includes(job.status)),
  );

  const talentColumns: ColumnsType<TalentType> = [
    {
      title: "",
      key: "list",
      render: (_, record) => {
        const job = getCurrentJob(record.result.talent.work_histories);
        const relevantSkills = JSON.parse(record.result.relevantSkills || "[]");
        const skills =
          Array.isArray(relevantSkills) && relevantSkills.length > 0
            ? relevantSkills.join(", ")
            : "No skills available";

        return (
          <div className="bg-white">
            <ul className="space-y-2 text-sm text-gray-800">
              {/* Job Title */}
              <li className="flex items-center gap-2 text-primary font-semibold">
                <i className="ri-briefcase-line text-lg"></i>
                <span>
                  Job Title:{" "}
                  <Link
                    to={routePath.jobDetails(record.result.listing.slug).path}
                    className="hover:underline"
                  >
                    {record.result.listing.title}
                  </Link>
                </span>
              </li>

              <li className="flex items-center gap-2">
                <i className="ri-user-line text-lg text-primary"></i>
                <span>
                  Talent Name:{" "}
                  <Link
                    to={routePath.talentDetails(record.result.talent.id).path}
                    className="text-primary hover:underline"
                  >
                    {record.result.talent.first_name}{" "}
                    {record.result.talent.last_name}{" "}
                    {record.result.talent.middle_name}
                  </Link>
                </span>
              </li>

              {/* Match % */}
              <li className="flex items-center gap-2">
                <i className="ri-percent-line text-lg text-green-600"></i>
                <span>Match: {record.result.score}%</span>
              </li>

              {/* Current Job */}
              <li className="flex items-center gap-2">
                <i className="ri-briefcase-2-line text-lg text-gray-600"></i>
                <span>Current Job: {job || "No Current Job"}</span>
              </li>

              {/* Key Skills */}
              <li className="flex items-center gap-2">
                <i className="ri-star-line text-lg text-yellow-500"></i>
                <span>Key Skills Matched: {skills}</span>
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  const getCurrentJob = (workHistories: any[]) => {
    const currentJob = workHistories?.find((job) => job.is_current === 1);
    return currentJob ? currentJob.title : null;
  };

  const filteredTalent = dataRec?.filter((talent) => {
    const name =
      `${talent.result.talent.first_name} ${talent.result.talent.last_name}`.toLowerCase();

    const currentJob =
      getCurrentJob(talent.result.talent.work_histories)?.toLowerCase() || "";

    const skills = Array.isArray(talent.result.relevantSkills)
      ? talent.result.relevantSkills.join(" ").toLowerCase()
      : "";

    const search = talentSearch.toLowerCase();

    return (
      name.includes(search) ||
      currentJob.includes(search) ||
      skills.includes(search)
    );
  });

  const topTalents = filteredTalent
    ?.filter((talent) => (talent.result.score || 0) >= 65)
    .sort((a, b) => (b.result.score || 0) - (a.result.score || 0))
    .slice(0, 7);

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mb-4 mt-4">
        <div className="bg-white rounded-lg shadow-md h-full p-4 sm:p-6 lg:col-span-4">
          <div>
            <h3 className="text-base font-bold">Hiring Metrics</h3>
          </div>
          <div className="relative">
            <div>
              <ul className="p-2 divide-y divide-gray-200 w-full">
                {/* Row 1 */}
                <li className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-0 sm:divide-x sm:divide-gray-200/70 py-4">
                  {/* Offer Acceptance Rate */}
                  <div className="flex flex-col w-full py-1 sm:pr-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-check-fill text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              Offer Acceptance Rate
                            </div>
                          </div>
                          <div className="text-end font-bold text-green-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.acceptanceRate}%
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Percentage of job offers accepted by talents.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Offer Decline Rate */}
                  <div className="flex flex-col w-full py-1 sm:pl-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-thumb-down-line text-xl text-red-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              Offer Decline Rate
                            </div>
                          </div>
                          <div className="text-end font-bold text-red-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.offerDeclineRate}%
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Percentage of job offers declined by talents.
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                {/* Row 2 */}
                <li className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-0 sm:divide-x sm:divide-gray-200/70 py-4">
                  {/* Hires */}
                  <div className="flex flex-col w-full py-1 sm:pr-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-user-2-fill text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              Hires
                            </div>
                          </div>
                          <div className="text-end font-bold text-green-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.number_of_hires}
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Number of talents hired through the platform.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Job Vacancy Duration */}
                  <div className="flex flex-col w-full py-1 sm:pl-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-briefcase-4-line text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              Job Vacancy Duration
                            </div>
                          </div>
                          <div className="text-end font-bold text-green-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.jobVacancyDuration}
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Average length of time job positions remain open.
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                {/* Row 3 */}
                <li className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-0 sm:divide-x sm:divide-gray-200/70 py-4">
                  {/* Job Posting Fill Rate */}
                  <div className="flex flex-col w-full py-1 sm:pr-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-briefcase-4-fill text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              Job Posting Fill Rate
                            </div>
                          </div>
                          <div className="text-end font-bold text-green-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.jobPostingFillRate}%
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Percentage of job postings successfully filled.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Likes */}
                  <div className="flex flex-col w-full py-1 sm:pl-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-heart-2-fill text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              Total Number of Applied Jobs
                            </div>
                          </div>
                          <div className="text-end font-bold text-indigo-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.totalLikes}
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Total number of applications on job postings.
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                {/* Row 4 */}
                <li className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-0 sm:divide-x sm:divide-gray-200/70 py-4">
                  <div className="flex flex-col w-full py-1 sm:pr-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-calendar-check-fill text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              <Link
                                to={routePath.recruierInvites}
                                className="hover:text-indigo-600"
                              >
                                Interviews Scheduled This Week
                              </Link>
                            </div>
                          </div>
                          <div className="text-end font-bold text-indigo-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.interviewsScheduledThisWeek}
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Number of interviews scheduled this week.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Jobs Posted */}
                  <div className="flex flex-col w-full py-1 sm:pl-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-briefcase-3-line text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              <Link
                                to={routePath.jobs}
                                className="hover:text-indigo-600"
                              >
                                Jobs Posted
                              </Link>
                            </div>
                          </div>
                          <div className="text-end font-bold text-indigo-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.jobsPosted}
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Number of jobs posted on the platform.
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                {/* Row 5 */}
                <li className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-0 sm:divide-x sm:divide-gray-200/70 py-4">
                  {/* Average Time to Hire */}
                  <div className="flex flex-col w-full py-1 sm:pr-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-time-line text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              Average Time to Hire
                            </div>
                          </div>
                          <div className="text-end font-bold text-indigo-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.averageTimeToHire} days
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Average time taken to complete hiring process.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Roles Currently Open */}
                  <div className="flex flex-col w-full py-1 sm:pl-6 text-sm font-semibold">
                    <div className="flex flex-col items-start w-full">
                      <span className="rounded-full w-[42px] h-[42px] bg-gray-100 flex items-center justify-center mb-3">
                        <i className="ri-archive-line text-xl text-indigo-500"></i>
                      </span>
                      <div className="flex w-full flex-col">
                        <div className="flex w-full justify-between items-center gap-2">
                          <div className="text-start flex-1">
                            <div className="mb-0.5 text-gray-800 text-sm sm:text-base">
                              <Link
                                to={routePath.jobs}
                                className="hover:text-indigo-600"
                              >
                                Roles Currently Open
                              </Link>
                            </div>
                          </div>
                          <div className="text-end font-bold text-indigo-500 text-sm sm:text-base whitespace-nowrap">
                            {stats?.stats.activeJobs}
                          </div>
                        </div>
                        <div className="text-gray-500 text-[10px] sm:text-xs mt-1">
                          Total number of roles currently open for hiring.
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="max-w-full lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md h-full p-6">
            <div className="pb-1 sm:justify-between sm:items-center gap-4">
              <h3 className="text-base font-bold mb-4">
                <Link to={routePath.jobs}>All Jobs Posted</Link>
              </h3>
              <div className="relative w-full sm:w-auto">
                <SearchInput
                  placeholder="Search Jobs"
                  value={jobsSearch}
                  onChange={(e) => setJobsSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="relative">
              <Table
                columns={jobsColumns}
                dataSource={filteredJobs}
                pagination={false}
                showHeader={false}
                scroll={{ y: 650 }}
                locale={{
                  emptyText: !isLoading && (
                    <CustomEmpty message="No record found" />
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-11 gap-4 mb-4 mt-12">
        <div className="grid lg:col-span-4 gap-4 mb-4">
          <div className="max-w-full">
            <div className="bg-white rounded-lg shadow-md h-full p-6">
              <div className="pb-6 justify-between items-center">
                <h3 className="text-base font-bold mb-4">
                  Talent Recommendations
                </h3>
                <SearchInput
                  placeholder="Search Talents"
                  value={talentSearch}
                  onChange={(e) => setTalentSearch(e.target.value)}
                />
              </div>
              <div className="relative">
                <Table
                  columns={talentColumns}
                  dataSource={topTalents}
                  pagination={false}
                  showHeader={false}
                  scroll={{ y: 350 }}
                  locale={{
                    emptyText: (
                      <CustomEmpty message="No recommended talent yet" />
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {userInfo?.type === "recruiter" &&
          userInfo?.recruiter?.is_corporate !== 1 && (
            <div className="lg:col-span-7 gap-4 mb-4">
              <div className="max-w-full h-full">
                <div className="bg-white border border-gray-200 rounded-xl h-full p-4 sm:p-6">
                  <div className="pb-4">
                    <h3 className="text-base font-semibold text-gray-800">
                      Messages Sent and Received
                    </h3>
                  </div>

                  <div className="relative w-full overflow-x-auto">
                    <div className="min-w-[300px] sm:min-w-0">
                      <Chart
                        options={options}
                        series={series}
                        type="line"
                        height={chartHeight}
                        width="100%"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        {showPopup && allJobs.length > 0 && (
          <FeedbackPopup
            onClose={handleClosePopup}
            jobs={allJobs}
            onProvideFeedback={handleProvideFeedback}
          />
        )}
      </div>
    </div>
  );
};
